import { axiosInstance } from "./axiosInstance.js";
import { getToken } from "./authToken.jsx";
import {
  resolveActiveMsalAccount,
  resolveApiScopesForAccount,
} from "../authConfig.js";
import { getImpersonationForAccount } from "../authImpersonation.js";
import { isE2EAuthBypassEnabled } from "../e2eAuthBypass.js";
import {
  isAggregatorPortalHost,
  readSelectedAggregatorGrower,
} from "../portal/aggregatorGrowerContext.js";
import { resolvePortalHost } from "../portal/portalUtils.js";
import { announceSessionIssue, sessionAccountKey, sessionContextChanged } from "./Auth/sessionRecovery.js";
import { getPortalSessionForAccount, getPortalSessionVersion } from "../portal/portalSession.js";

const GRAPH_SCOPES = ["User.Read"];
const TRUSTED_GRAPH_HOSTS = new Set(["graph.microsoft.com"]);
// Isolated fake identity, not a real developer/grower email -- the backend
// auto-provisions a real, isolated GrowerProfileRecord for this address, dev/test only.
// See docs/playwright-e2e.md for the full contract (including the optional
// X-Playwright-User-Email override for per-test isolation, set just below).
const PLAYWRIGHT_TEST_EMAIL = "playwright-autonomous@playwright.fieldtomarket.ca";
export const PLAYWRIGHT_TEST_EMAIL_KEY = "fieldprint.playwright.userEmail";

function readPlaywrightTestEmail() {
  const override = window.localStorage
    .getItem(PLAYWRIGHT_TEST_EMAIL_KEY)
    ?.trim()
    .toLowerCase();

  return override?.endsWith("@playwright.fieldtomarket.ca")
    ? override
    : PLAYWRIGHT_TEST_EMAIL;
}

let requestInterceptorId = null;
let responseInterceptorId = null;

function isGraphUrl(url) {
  if (!url) {
    return false;
  }

  try {
    const parsedUrl = new URL(url);
    const host = parsedUrl.hostname.toLowerCase();

    return (
      TRUSTED_GRAPH_HOSTS.has(host) ||
      Array.from(TRUSTED_GRAPH_HOSTS).some((trustedHost) =>
        host.endsWith(`.${trustedHost}`)
      )
    );
  } catch (error) {
    return false;
  }
}

function requestContext(msalInstance, url) {
  const account = resolveActiveMsalAccount(msalInstance);
  const graph = isGraphUrl(url);
  return {
    account,
    impersonation: graph ? null : getImpersonationForAccount(account),
    portal: graph ? null : getPortalSessionForAccount(account).portal || resolvePortalHost().portal,
    simulationVersion: graph ? null : getPortalSessionVersion(),
    grower: !graph && isAggregatorPortalHost(window.location.hostname, process.env, account)
      ? readSelectedAggregatorGrower()?.grower_profile_public_id || null : null,
    graph,
  };
}

function contextKey(context) {
  return JSON.stringify([sessionAccountKey(context.account), context.portal, context.simulationVersion, context.impersonation?.impersonatedEmail || null, context.grower]);
}

export function attachAuthInterceptors(msalInstance) {
  if (requestInterceptorId !== null) {
    axiosInstance.interceptors.request.eject(requestInterceptorId);
  }

  if (responseInterceptorId !== null) {
    axiosInstance.interceptors.response.eject(responseInterceptorId);
  }

  requestInterceptorId = axiosInstance.interceptors.request.use(
    async (config) => {
      config.headers = config.headers || {};

      if (isE2EAuthBypassEnabled() && !isGraphUrl(config.url)) {
        delete config.headers.Authorization;
        delete config.headers.provider;
        delete config.headers.token;
        delete config.headers["X-Impersonate-Email"];
        delete config.headers["X-Grower-Profile-Public-Id"];
        config.headers["X-Playwright-Autonomous-Test"] = "true";
        config.headers["X-Playwright-User-Email"] = readPlaywrightTestEmail();
        config.headers["X-Playwright-Portal"] = resolvePortalHost(
          window.location.hostname
        ).portal;
        if (isAggregatorPortalHost(window.location.hostname)) {
          const selectedGrower = readSelectedAggregatorGrower();
          if (selectedGrower?.grower_profile_public_id) {
            config.headers["X-Grower-Profile-Public-Id"] =
              selectedGrower.grower_profile_public_id;
          }
        }
        return config;
      }

      const context = requestContext(msalInstance, config.url);
      const capturedKey = contextKey(context);
      if (config._ftmcAuthContext && config._ftmcAuthContext !== capturedKey) throw sessionContextChanged();
      config._ftmcAuthContext = capturedKey;
      const activeAccount = context.account;
      const scopes = context.graph
        ? GRAPH_SCOPES
        : resolveApiScopesForAccount(activeAccount);
      let token;
      try {
        token = await getToken(msalInstance, scopes, activeAccount);
      } catch (error) {
        if (contextKey(requestContext(msalInstance, config.url)) !== capturedKey) throw sessionContextChanged();
        if (!context.graph) announceSessionIssue(error, { lane: resolvePortalHost().portal === "grower" ? "external" : "workforce" });
        throw error;
      }
      if (contextKey(requestContext(msalInstance, config.url)) !== capturedKey) throw sessionContextChanged();
      const activeImpersonation = context.impersonation;

      delete config.headers.provider;
      delete config.headers.token;
      delete config.headers["X-Impersonate-Email"];
      delete config.headers["X-Grower-Profile-Public-Id"];

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers.token = `Bearer ${token}`;
        config.headers.provider = "microsoft";

        if (activeImpersonation?.impersonatedEmail) {
          config.headers["X-Impersonate-Email"] =
            activeImpersonation.impersonatedEmail;
        }

        if (context.grower) config.headers["X-Grower-Profile-Public-Id"] = context.grower;
      } else {
        delete config.headers.Authorization;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  responseInterceptorId = axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error || {};
      const status = response?.status;

      if (!config || status !== 401) {
        return Promise.reject(error);
      }

      const originalContext = requestContext(msalInstance, config.url);
      const capturedKey = contextKey(originalContext);
      if (config._ftmcAuthContext && config._ftmcAuthContext !== capturedKey) return Promise.reject(sessionContextChanged());
      const preserveSession = (failure = error) => {
        if (!isGraphUrl(config.url)) {
          announceSessionIssue(failure, { stage: "api_retry", lane: resolvePortalHost().portal === "grower" ? "external" : "workforce", requestId: response.headers?.["x-request-id"] || config._ftmcTelemetry?.requestId });
        }
        return Promise.reject(error);
      };
      if (config._retry) return preserveSession();

      config._retry = true;

      const activeAccount = originalContext.account;
      const scopes = isGraphUrl(config.url)
        ? GRAPH_SCOPES
        : resolveApiScopesForAccount(activeAccount);

      try {
        const newToken = await getToken(msalInstance, scopes, activeAccount, true);

        if (contextKey(requestContext(msalInstance, config.url)) !== capturedKey) return Promise.reject(sessionContextChanged());

        if (newToken) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${newToken}`;
          config.headers.token = `Bearer ${newToken}`;
          config.headers.provider = "microsoft";
          return axiosInstance.request(config);
        }
      } catch (refreshError) {
        if (contextKey(requestContext(msalInstance, config.url)) !== capturedKey) return Promise.reject(sessionContextChanged());
        return preserveSession(refreshError);
      }

      return preserveSession();
    }
  );
}
